import * as Yup from 'yup'
import {BaseEntity} from '../../../../shared/models/ApiModels'
import {IHrProcessModel} from '../process/HrProcessModel'
import {ILocationModel} from '../location/LocationModel'
import {IQuestionModel} from '../question/QuestionModel'

export const HR_JOB_PATH = '/hr/job'
/** Giriş gerektirmeyen ilan sayfası; ilan id'si ile açılır: /jobs/{id} */
export const PUBLIC_JOB_PATH = '/jobs'

export type SectorType = 'IT' | 'FINANCE' | 'HEALTHCARE' | 'EDUCATION'
export type WorkType = 'REMOTE' | 'HYBRID' | 'ONSITE'
export type EmploymentType = 'FULL_TIME' | 'PART_TIME' | 'INTERN' | 'FREELANCE'
export type JobStatus = 'ACTIVE' | 'PASSIVE'

type Option<T extends string> = {value: T; label: string}

export const SECTOR_OPTIONS: Option<SectorType>[] = [
  {value: 'IT', label: 'Bilişim'},
  {value: 'FINANCE', label: 'Finans'},
  {value: 'HEALTHCARE', label: 'Sağlık'},
  {value: 'EDUCATION', label: 'Eğitim'},
]

export const WORK_TYPE_OPTIONS: Option<WorkType>[] = [
  {value: 'ONSITE', label: 'İş Yerinde'},
  {value: 'HYBRID', label: 'Hibrit'},
  {value: 'REMOTE', label: 'Uzaktan'},
]

export const EMPLOYMENT_TYPE_OPTIONS: Option<EmploymentType>[] = [
  {value: 'FULL_TIME', label: 'Tam Zamanlı'},
  {value: 'PART_TIME', label: 'Yarı Zamanlı'},
  {value: 'INTERN', label: 'Stajyer'},
  {value: 'FREELANCE', label: 'Serbest'},
]

export const STATUS_OPTIONS: Option<JobStatus>[] = [
  {value: 'ACTIVE', label: 'Yayında'},
  {value: 'PASSIVE', label: 'Pasif'},
]

export const optionLabel = <T extends string>(options: Option<T>[], value?: T | null) =>
  options.find((option) => option.value === value)?.label ?? value ?? ''

/** GET cevabı (JobDto) */
export interface IJobModel extends BaseEntity {
  title: string
  subTitle?: string | null
  description: string
  sector?: SectorType | null
  workType: WorkType
  employmentType: EmploymentType
  status: JobStatus
  hrProcess?: IHrProcessModel | null
  locationList: ILocationModel[]
  questionList: IQuestionModel[]
  createdDate?: string
}

/** create / update gövdesi (JobDao) */
export interface IJobRequest {
  title: string
  subTitle: string
  description: string
  sector: SectorType | null
  workType: WorkType
  employmentType: EmploymentType
  status: JobStatus
  hrProcessId: string | null
  locationIds: string[]
  questionIds: string[]
}

/** Public ilan sayfası cevabı (JobPublicDto) */
export interface IPublicJobModel {
  id: string
  corporateId: string
  title: string
  subTitle?: string | null
  description: string
  sector?: SectorType | null
  workType: WorkType
  employmentType: EmploymentType
  createdDate?: string
  locationList: ILocationModel[]
  questionList: IQuestionModel[]
}

export interface IJobFormValues {
  title: string
  subTitle: string
  description: string
  sector: SectorType | ''
  workType: WorkType | ''
  employmentType: EmploymentType | ''
  status: JobStatus
  hrProcessId: string
  locationIds: string[]
  questionIds: string[]
}

export const initialJobForm: IJobFormValues = {
  title: '',
  subTitle: '',
  description: '',
  sector: '',
  workType: '',
  employmentType: '',
  status: 'PASSIVE',
  hrProcessId: '',
  locationIds: [],
  questionIds: [],
}

/** Editör boşken bile "<p><br></p>" üretir; doluluk kontrolü düz metin üzerinden yapılır */
export const htmlToText = (html: string) => {
  const element = document.createElement('div')
  element.innerHTML = html
  return (element.textContent || '').trim()
}

export const validationJob = Yup.object().shape({
  title: Yup.string().trim().required('İlan başlığı zorunludur').max(255, 'En fazla 255 karakter'),
  subTitle: Yup.string().trim().max(255, 'En fazla 255 karakter'),
  description: Yup.string().test('not-empty', 'İlan açıklaması zorunludur', (value) =>
    value ? htmlToText(value).length > 0 : false
  ),
  workType: Yup.string().required('Çalışma şekli zorunludur'),
  employmentType: Yup.string().required('İstihdam tipi zorunludur'),
  locationIds: Yup.array().min(1, 'En az bir lokasyon seçilmelidir'),
})

export const toJobFormValues = (job: IJobModel): IJobFormValues => ({
  title: job.title,
  subTitle: job.subTitle ?? '',
  description: job.description,
  sector: job.sector ?? '',
  workType: job.workType,
  employmentType: job.employmentType,
  status: job.status,
  hrProcessId: job.hrProcess?.id ?? '',
  locationIds: job.locationList.map((location) => location.id || '').filter(Boolean),
  questionIds: job.questionList.map((question) => question.id || '').filter(Boolean),
})

export const toJobRequest = (values: IJobFormValues): IJobRequest => ({
  title: values.title.trim(),
  subTitle: values.subTitle.trim(),
  description: values.description,
  sector: values.sector || null,
  workType: values.workType as WorkType,
  employmentType: values.employmentType as EmploymentType,
  status: values.status,
  hrProcessId: values.hrProcessId || null,
  locationIds: values.locationIds,
  questionIds: values.questionIds,
})

export const publicJobUrl = (jobId: string) => `${window.location.origin}${PUBLIC_JOB_PATH}/${jobId}`

/** İlan dashboard'u: /hr/job/dashboard/{id} */
export const jobDashboardPath = (jobId: string) => `${HR_JOB_PATH}/dashboard/${jobId}`

/** JobDashboardDto */
export interface IJobDashboardModel {
  jobId: string
  title: string
  status: JobStatus
  createdDate?: string | null
  applicationCount: number
  /** Süreci olumlu Bitiş adımında tamamlanan */
  positiveCount: number
  /** Süreci olumsuz Bitiş adımında tamamlanan */
  negativeCount: number
  /** Başvuru sorularında koşulu sağlamayan */
  rejectedCount: number
  inProgressCount: number
  notStartedCount: number
  /** İlk başvurudan (ya da ilan tarihinden) bugüne gün gün; tarih YYYY-MM-DD */
  dailyApplications: {date: string; count: number}[]
}

/** Başvuru formu: /jobs/{id}/apply */
export const publicJobApplyPath = (jobId: string) => `${PUBLIC_JOB_PATH}/${jobId}/apply`
